import { ReactNode } from "react";

import { Link } from "react-router-dom";

import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";

import classNames from "../../utils/classNames";

interface OverviewKpiCardProps {
  label: string;
  value: ReactNode;
  subValue?: ReactNode;
  footer?: ReactNode;
  // "warning" tints the card amber - for a figure that wants acting on rather than reading.
  tone?: "default" | "warning";
  // Makes the whole card a router link (e.g. a count that opens the list behind it).
  to?: string;
}

export default function OverviewKpiCard({
  label,
  value,
  subValue,
  footer,
  tone = "default",
  to,
}: OverviewKpiCardProps) {
  const isWarning = tone === "warning";
  const cardClasses = classNames(
    "flex-1 border rounded-md p-4 min-w-[140px] relative",
    isWarning ? "bg-amber-50 border-amber-200" : "bg-white border-gray-200",
    to && "block hover:border-gray-300 hover:shadow-sm"
  );

  const body = (
    <>
      {to && (
        <ArrowTopRightOnSquareIcon className="absolute top-3 right-3 h-3.5 w-3.5 text-gray-400" />
      )}
      <div
        className={classNames(
          "text-[11px] font-semibold uppercase tracking-wide",
          isWarning ? "text-amber-800" : "text-gray-500"
        )}
      >
        {label}
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <div
          className={classNames(
            "text-2xl font-semibold",
            isWarning ? "text-amber-900" : "text-gray-900"
          )}
        >
          {value}
        </div>
        {subValue && (
          <div className={classNames("text-sm", isWarning ? "text-amber-800" : "text-gray-500")}>
            {subValue}
          </div>
        )}
      </div>
      {footer && (
        <div className={classNames("mt-2 text-xs", isWarning ? "text-amber-800" : "text-gray-500")}>
          {footer}
        </div>
      )}
    </>
  );

  return to ? (
    <Link to={to} className={cardClasses}>
      {body}
    </Link>
  ) : (
    <div className={cardClasses}>{body}</div>
  );
}
